import type { ComponentSchema, FieldSchema } from '../../../../../schema-types'

/**
 * Home sections Mohalla renders with its own markup (sections/<type>/themes/apparels-5.vue), described by
 * the fields those overrides read. Colour fields the overrides ignore are left out: the theme keeps its own
 * tokens.
 */

const CONTENT_PAGES = ['home', 'shop', 'category', 'categories', 'product', 'cart', 'blog']

/** Icons Mohalla's kit draws; shared names (shipping, secure, refund, support) map onto them too. */
const ICON_OPTIONS = ['truck', 'cash', 'phone', 'shield', 'star', 'heart', 'package', 'check', 'store', 'map-pin', 'mail', 'sparkle', 'clock', 'bag', 'user', 'search', 'refresh', 'lock', 'tag']
  .map(value => ({ label: value.replace('-', ' '), value }))

const headingFields = (defaults: { eyebrow?: string, title?: string, subtitle?: string } = {}): FieldSchema[] => [
  { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: defaults.eyebrow ?? '', help: 'Small line above the title.' },
  { key: 'title', label: 'Title', type: 'localized-string', default: defaults.title ?? '' },
  { key: 'subtitle', label: 'Subtitle', type: 'localized-text', default: defaults.subtitle ?? '' },
]

const POINT_SCHEMA: FieldSchema[] = [
  { key: 'icon', label: 'Icon', type: 'icon', default: 'check', options: ICON_OPTIONS },
  { key: 'title', label: 'Title', type: 'localized-string' },
  { key: 'body', label: 'Text', type: 'localized-text' },
]

const SLIDE_TEXT_FIELDS: FieldSchema[] = [
  { key: 'alt', label: 'Picture description', type: 'string', help: 'What the picture shows, for screen readers.' },
  { key: 'title', label: 'Title over the photo', type: 'string', help: 'Optional, a few words.' },
  { key: 'subtitle', label: 'Line under the title', type: 'string' },
  { key: 'ctaText', label: 'Button text', type: 'string' },
  { key: 'ctaLink', label: 'Button link', type: 'string', placeholder: '/shop' },
]

export const bannerCarouselSchema: ComponentSchema = {
  type: 'banner-carousel',
  label: 'Banner carousel',
  description: 'Photo slides with an optional short title, line and button over the photo: full-width slides, or a row of tall posters.',
  supportedPages: ['home', 'shop', 'category', 'categories'],
  variants: [
    {
      id: 'v1',
      label: 'Full-width photo slides',
      fields: [
        {
          key: 'slides',
          label: 'Slides',
          type: 'array',
          itemSchema: [
            { key: 'image', label: 'Picture', type: 'image', required: true, help: 'Wide, about 1920 × 550 px.' },
            { key: 'mobileImage', label: 'Phone picture', type: 'image', help: 'Optional, about 780 × 600 px.' },
            ...SLIDE_TEXT_FIELDS,
            { key: 'cta2Text', label: 'Second button text', type: 'string' },
            { key: 'cta2Link', label: 'Second button link', type: 'string', placeholder: '/categories' },
            { key: 'link', label: 'Link of the whole slide', type: 'string', placeholder: '/shop', help: 'Used when the slide has no button.' },
          ],
          default: [],
        },
      ],
    },
    {
      id: 'v2',
      label: 'Poster row',
      fields: [
        {
          key: 'slides',
          label: 'Posters',
          type: 'array',
          help: 'Three show side by side on a computer, one on a phone; the next one peeks in.',
          itemSchema: [
            { key: 'image', label: 'Picture', type: 'image', required: true, help: 'Tall, about 960 × 1200 px (4:5).' },
            ...SLIDE_TEXT_FIELDS,
            { key: 'link', label: 'Link of the whole poster', type: 'string', placeholder: '/shop', help: 'Used when the poster has no button.' },
          ],
          default: [],
        },
      ],
    },
  ],
  commonFields: [
    { key: 'title', label: 'Name for screen readers', type: 'string', default: 'Offers' },
    { key: 'autoplay', label: 'Play automatically', type: 'boolean', default: true },
    { key: 'autoplaySeconds', label: 'Seconds per slide', type: 'number', default: 5, dependency: { field: 'autoplay', value: true } },
  ],
}

export const categoryBentoSchema: ComponentSchema = {
  type: 'category-bento',
  label: 'Category mosaic',
  description: 'Portrait photo tiles linking to categories, the name on a small label at the foot of each: the tiles you list, else the store’s categories.',
  supportedPages: CONTENT_PAGES,
  variants: [
    { id: 'v1', label: 'Portrait tiles with a label' },
    { id: 'v2', label: 'Mosaic (first tile large)' },
  ],
  commonFields: [
    ...headingFields(),
    {
      key: 'tiles',
      label: 'Tiles',
      type: 'array',
      help: 'Empty = the store’s categories.',
      itemSchema: [
        { key: 'categoryId', label: 'Category', type: 'category-select', help: 'Sets the link, name and picture unless filled below.' },
        { key: 'title', label: 'Name', type: 'localized-string' },
        { key: 'tagline', label: 'Tagline (mosaic)', type: 'localized-string' },
        { key: 'image', label: 'Picture', type: 'image' },
        { key: 'link', label: 'Link', type: 'string', placeholder: '/category/…' },
      ],
      default: [],
    },
    { key: 'limit', label: 'Categories shown (automatic tiles)', type: 'number', default: 12 },
  ],
}

export const comboSpotlightSchema: ComponentSchema = {
  type: 'combo-spotlight',
  label: 'Product spotlight',
  description: 'One product or bundle on a card: large photo, price, saving and Add to bag.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Photo and details card' }],
  commonFields: [
    ...headingFields({ eyebrow: 'Combo deal', title: 'Pick of the week' }),
    { key: 'productId', label: 'Product', type: 'product-select', help: 'Hidden until a product is picked.' },
    { key: 'description', label: 'Text', type: 'localized-text', default: '' },
    { key: 'mrpLabel', label: 'Regular price label', type: 'localized-string', default: 'Regular price' },
    { key: 'savingLabel', label: 'Saving label', type: 'localized-string', default: 'You save' },
    { key: 'detailsLabel', label: 'Details link text', type: 'localized-string', default: 'View details' },
  ],
}

export const corporateBandSchema: ComponentSchema = {
  type: 'corporate-band',
  label: 'Bulk order band',
  description: 'A warm box with a linked title, a short pitch and a few product pictures, e.g. for bulk, team or wholesale orders.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Box with pictures' }],
  commonFields: [
    ...headingFields({ title: 'Bulk order / wholesale' }).filter(f => f.key !== 'subtitle'),
    { key: 'text', label: 'Text', type: 'localized-text', default: 'Plain or printed T-shirts and polos for your team, school or event, at wholesale prices and with no minimum order.' },
    { key: 'cta.url', label: 'Title link', type: 'string', default: '/contact' },
    {
      key: 'images',
      label: 'Pictures',
      type: 'array',
      help: 'Up to six, square.',
      itemSchema: [
        { key: 'image', label: 'Picture', type: 'image' },
        { key: 'alt', label: 'Picture description', type: 'string' },
      ],
      default: [],
    },
  ],
}

export const productRailSchema: ComponentSchema = {
  type: 'product-rail',
  label: 'Product rail',
  description: 'Product cards from new arrivals, deals, a category or your own pick, as a grid or a swipe row.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Product cards' }],
  commonFields: [
    ...headingFields({ eyebrow: 'Just dropped', title: 'New arrivals' }),
    { key: 'source', label: 'Products', type: 'product-source', default: { source: 'new', limit: 8 } },
    {
      key: 'layout',
      label: 'Layout',
      type: 'select',
      options: [
        { label: 'Grid (four up)', value: 'grid' },
        { label: 'Swipe row', value: 'rail' },
      ],
      default: 'grid',
    },
    { key: 'showViewAll', label: 'Show “View all”', type: 'boolean', default: true },
    { key: 'viewAllLabel', label: '“View all” text', type: 'string', default: 'View all', dependency: { field: 'showViewAll', value: true } },
  ],
}

export const promisesSchema: ComponentSchema = {
  type: 'promises',
  label: 'Promises strip',
  description: 'Store promises: a grey band of icon, title and line, or a numbered checklist on a black band.',
  supportedPages: [...CONTENT_PAGES, 'about', 'contact'],
  variants: [
    {
      id: 'v1',
      label: 'Grey band',
      fields: [
        {
          key: 'items',
          label: 'Promises',
          type: 'array',
          itemSchema: POINT_SCHEMA,
          default: [
            { icon: 'truck', title: 'Fast delivery', body: 'Quick delivery across the city.' },
            { icon: 'cash', title: 'Pay on delivery', body: 'Pay in cash when your order arrives.' },
            { icon: 'refresh', title: 'Easy returns', body: 'Wrong size? Swap it within 7 days.' },
            { icon: 'phone', title: 'Here to help', body: 'Call or message us any day.' },
          ],
        },
      ],
    },
    {
      id: 'v2',
      label: 'Checklist on a black band',
      fields: [
        { key: 'highlight', label: 'Highlighted words', type: 'localized-string', default: 'anything you need.', help: 'Shown in turmeric on their own line under the title.' },
        {
          key: 'items',
          label: 'Checklist',
          type: 'array',
          itemSchema: POINT_SCHEMA.filter(field => field.key !== 'icon'),
          default: [
            { title: 'Order status and tracking' },
            { title: 'Size, fit and product advice' },
            { title: 'Delivery, swaps and payments' },
            { title: 'Bulk, team and custom orders' },
          ],
        },
      ],
    },
  ],
  commonFields: [...headingFields()],
}

export const reviewsSchema: ComponentSchema = {
  type: 'reviews',
  label: 'Customer reviews',
  description: 'A swipe row of quote cards: rating, title, quote, name and the product. Store reviews replace these once available.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Quote cards' }],
  commonFields: [
    ...headingFields({ title: 'What our customers say' }),
    {
      key: 'items',
      label: 'Reviews',
      type: 'array',
      help: 'Real reviews from your customers, with their permission.',
      itemSchema: [
        { key: 'rating', label: 'Rating (1–5)', type: 'number', default: 5 },
        { key: 'title', label: 'Title', type: 'localized-string' },
        { key: 'body', label: 'Review', type: 'localized-text' },
        { key: 'name', label: 'Customer name', type: 'string' },
        { key: 'productId', label: 'Product', type: 'product-select' },
      ],
      default: [],
    },
    { key: 'limit', label: 'Most reviews shown', type: 'number', default: 6 },
    { key: 'minRating', label: 'Lowest rating shown (store reviews)', type: 'number', default: 4 },
  ],
}

export const storySchema: ComponentSchema = {
  type: 'story',
  label: 'Story band',
  description: 'An off-white split: eyebrow and a huge headline with a highlighted last line (and an optional photo) beside a bold line, the paragraph and a numbered list.',
  supportedPages: [...CONTENT_PAGES, 'about', 'contact'],
  variants: [{ id: 'v1', label: 'Headline beside a numbered list' }],
  commonFields: [
    ...headingFields({ title: 'Our story', subtitle: 'Comfortable clothes you can wear every day.' }).map(f => (f.key === 'subtitle' ? { ...f, label: 'Bold line' } : f)),
    { key: 'highlight', label: 'Highlighted words', type: 'localized-string', default: 'to your street.', help: 'Shown in amber on their own line under the title.' },
    { key: 'text', label: 'Paragraph', type: 'localized-text', default: 'We choose soft, breathable fabrics, check every stitch and size, and price fairly, so your everyday clothes look good and last.' },
    { key: 'image', label: 'Picture', type: 'image', help: 'Optional, under the headline; landscape 4:3.' },
    {
      key: 'points',
      label: 'Points',
      type: 'array',
      itemSchema: POINT_SCHEMA.filter(field => field.key !== 'icon'),
      help: 'Shown as a numbered list (01, 02…).',
      default: [],
    },
    { key: 'ctaLink', label: 'Link', type: 'string', default: '/about', help: 'The link under the list goes here.' },
    { key: 'ctaLabel', label: 'Link text', type: 'localized-string', default: '' },
  ],
}
